'use client'
import React, { useState } from 'react'
import Image from 'next/image'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination } from 'swiper/modules'
import Gallery from '@components/Gallery'
import VideoGallery from '@components/VideoGallery'
import YoutubePreview from '@components/YoutubePreview'
import { IoMdPhotos, IoMdVideocam } from 'react-icons/io'
import { MdViewInAr } from 'react-icons/md'
import { TbLayout } from 'react-icons/tb'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

// ─── Ratio helper ──────────────────────────────────────────────────────────────
// config.ratio is stored as '4/3', '3/2', '16/9', '1/1'
const RATIO_CLASS = {
  '4/3':  'aspect-[4/3]',
  '3/2':  'aspect-[3/2]',
  '16/9': 'aspect-[16/9]',
  '1/1':  'aspect-square',
}
const ratioClass = (ratio) => RATIO_CLASS[ratio] ?? 'aspect-[4/3]'


// ─── Count overlay styles ──────────────────────────────────────────────────────
function CountOverlay({ count, countStyle = 'dark-corner', dictionary }) {
  const label = `${count} ${dictionary?.photos ?? 'photos'}`
  if (countStyle === 'none') return null
  if (countStyle === 'dark-corner') return (
    <div className="absolute bottom-0 right-0 bg-black/60 text-white text-sm font-semibold px-3 py-1" style={{ borderTopLeftRadius: '0.5rem' }}>
      <IoMdPhotos className="inline mr-1 mb-0.5" />{label}
    </div>
  )
  if (countStyle === 'dark-full') return (
    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
      <span className="text-white text-lg font-bold"><IoMdPhotos className="inline mr-1 mb-0.5" />{label}</span>
    </div>
  )
  if (countStyle === 'pill') return (
    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 text-white text-sm font-semibold px-3 py-1 rounded-full">
      <IoMdPhotos className="inline mr-1 mb-0.5" />{label}
    </div>
  )
  if (countStyle === 'bar') return (
    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-sm font-semibold px-3 py-2 text-center">
      <IoMdPhotos className="inline mr-1 mb-0.5" />{label}
    </div>
  )
  // default fallback = dark-corner
  return (
    <div className="absolute bottom-0 right-0 bg-black/60 text-white text-sm font-semibold px-3 py-1" style={{ borderTopLeftRadius: '0.5rem' }}>
      <IoMdPhotos className="inline mr-1 mb-0.5" />{label}
    </div>
  )
}

// ─── Shared: image tile ────────────────────────────────────────────────────────
function ImgTile({ src, alt, onClick, fill = true, className = '', overlay = null, radius = '0' }) {
  return (
    <div className={`relative w-full overflow-hidden cursor-pointer hover:opacity-90 transition-opacity ${className}`} style={{ borderRadius: radius }} onClick={onClick}>
      <Image
        src={src}
        alt={alt}
        fill={fill}
        className="object-cover"
        placeholder="blur"
        blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAAAyCAYAAACqNX6+AAAAXUlEQVR42u3RMQEAAAgDoJnc6FrB0wMqUMl0eKOECEGIEIQIQYgQhAgRIgQhQhAiBCFCECIEIQgRghAhCBGCECEIQYgQhAhBiBCECEEIQoQgRAhChCBECEIQIoSrBfgKSs//8LAxAAAAAElFTkSuQmCC"
      />
      {overlay}
    </div>
  )
}

// ─── Shared: media buttons bar ─────────────────────────────────────────────────
function MediaButtons({ config, onPhotos, onVideo, videoUrl, vrTour, blueprints, dictionary }) {
  return (
    <div className="flex flex-row gap-1">

      {config.showVideo && videoUrl && (
        <button
          className="flex flex-row items-center bg-black/50 hover:bg-black/70 rounded-md text-white px-2 py-1 text-sm transition-colors"
          onClick={onVideo}
        >
          <IoMdVideocam className="mr-1" />
          {dictionary?.video ?? 'Video'}
        </button>
      )}
      {config.showVR && vrTour && (
        <a
          href={vrTour}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-row items-center bg-black/50 hover:bg-black/70 rounded-md text-white px-2 py-1 text-sm transition-colors"
        >
          <MdViewInAr className="mr-1" />
          {dictionary?.vr ?? 'VR Tour'}
        </a>
      )}
      {config.showBlueprint && blueprints?.length > 0 && (
        <button
          className="flex flex-row items-center bg-black/50 hover:bg-black/70 rounded-md text-white px-2 py-1 text-sm transition-colors"
          onClick={() => {/* handled by parent */}}
        >
          <TbLayout className="mr-1" />
          {dictionary?.blueprints ?? 'Floor Plans'}
        </button>
      )}
    </div>
  )
}

// ─── Style: single carousel ────────────────────────────────────────────────────
function StyleSingle({ config, images, videoUrl, vrTour, blueprints, promotionTitle, altName, onOpenPopup, onOpenVideo, dictionary, radius = '0.5rem' }) {
  const ratio = ratioClass(config.ratio)
  return (
    <>
      {/* Desktop */}
      <div className="hidden sm:block w-full">
        <Swiper className="w-full" navigation={true} modules={[Navigation]}>
          {images.map((img, i) => (
            <SwiperSlide key={i}>
              <div className={`relative w-full ${ratio}`} style={{ borderRadius: radius, overflow: 'hidden' }}>
                {i === 0 && promotionTitle && (
                  <div className="absolute left-2 top-2 bg-third text-white px-2 py-1 rounded-md z-10 font-semibold text-sm">{promotionTitle}</div>
                )}
                <Image
                  src={img}
                  alt={`${altName} ${i + 1}`}
                  fill
                  className="object-cover cursor-pointer"
                  onClick={() => onOpenPopup(i)}
                  priority={i === 0}
                  placeholder="blur"
                  blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAAAyCAYAAACqNX6+AAAAXUlEQVR42u3RMQEAAAgDoJnc6FrB0wMqUMl0eKOECEGIEIQIQYgQhAgRIgQhQhAiBCFCECIEIQgRghAhCBGCECEIQYgQhAhBiBCECEEIQoQgRAhChCBECEIQIoSrBfgKSs//8LAxAAAAAElFTkSuQmCC"
                />
                <div className="absolute z-10 bottom-2 right-2">
                  <MediaButtons config={config} onPhotos={() => onOpenPopup(i)} onVideo={onOpenVideo} videoUrl={videoUrl} vrTour={vrTour} blueprints={blueprints} dictionary={dictionary} />
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      {/* Mobile */}
      <MobileSwiper images={images} altName={altName} onOpen={onOpenPopup} promotionTitle={promotionTitle} />
    </>
  )
}

// ─── Style: multi 1+4 ─────────────────────────────────────────────────────────
function StyleMulti4({ config, images, videoUrl, vrTour, blueprints, promotionTitle, altName, onOpenPopup, onOpenVideo, dictionary, radius = '0.5rem' }) {
  const ratio = ratioClass(config.ratio)
  const canShowVideo = !config.style.includes('_nov')
  const firstSlotIsVideo = canShowVideo && !!videoUrl

  return (
    <>
      {/* Desktop */}
      <div className="hidden sm:flex w-full flex-row gap-1">
        {/* Left: main image or video */}
        <div className={`w-1/2 relative ${ratio}`} style={{ borderRadius: radius, overflow: 'hidden' }}>
          {promotionTitle && !firstSlotIsVideo && (
            <div className="absolute left-2 top-2 bg-third text-white px-2 py-1 rounded-md z-10 font-semibold text-sm">{promotionTitle}</div>
          )}
          {firstSlotIsVideo ? (
            <YoutubePreview url={videoUrl} height="100%" width="100%" />
          ) : (
            <ImgTile src={images[0]} alt={`${altName} 1`} onClick={() => onOpenPopup(0)} className="absolute inset-0 w-full h-full" radius={radius} />
          )}
        </div>
        {/* Right: 2x2 grid */}
        <div className="w-1/2 grid grid-cols-2 grid-rows-2 gap-1">
          {[0, 1, 2, 3].map(i => {
            const imgIndex = firstSlotIsVideo ? i : i + 1
            const img = images[imgIndex]
            if (!img) return <div key={i} className="bg-gray-100 rounded-md" />
            const isLast = i === 3
            return (
              <div key={i} className={`relative ${ratio}`} style={{ borderRadius: radius, overflow: 'hidden' }}>
                <ImgTile
                  src={img}
                  alt={`${altName} ${imgIndex + 1}`}
                  onClick={() => onOpenPopup(imgIndex)}
                  className="absolute inset-0 w-full h-full"
                  radius={radius}
                  overlay={isLast ? <CountOverlay count={images.length} countStyle={config.countStyle} dictionary={dictionary} /> : null}
                />
              </div>
            )
          })}
        </div>
        {/* Buttons */}
        <div className="absolute z-10 bottom-2 right-2">
          <MediaButtons config={config} onPhotos={() => onOpenPopup(0)} onVideo={onOpenVideo} videoUrl={videoUrl} vrTour={vrTour} blueprints={blueprints} dictionary={dictionary} />
        </div>
      </div>
      {/* Mobile */}
      <MobileSwiper images={images} altName={altName} onOpen={onOpenPopup} promotionTitle={promotionTitle} />
    </>
  )
}

// ─── Style: multi 1+2 ─────────────────────────────────────────────────────────
function StyleMulti2({ config, images, videoUrl, vrTour, blueprints, promotionTitle, altName, onOpenPopup, onOpenVideo, dictionary, radius = '0.5rem' }) {
  const ratio = ratioClass(config.ratio)
  const canShowVideo = !config.style.includes('_nov')
  const firstSlotIsVideo = canShowVideo && !!videoUrl

  return (
    <>
      {/* Desktop */}
      <div className="hidden sm:flex w-full flex-row gap-1">
        {/* Left: main */}
        <div className={`w-2/3 relative ${ratio}`} style={{ borderRadius: radius, overflow: 'hidden' }}>
          {promotionTitle && !firstSlotIsVideo && (
            <div className="absolute left-2 top-2 bg-third text-white px-2 py-1 rounded-md z-10 font-semibold text-sm">{promotionTitle}</div>
          )}
          {firstSlotIsVideo ? (
            <YoutubePreview url={videoUrl} height="100%" width="100%" />
          ) : (
            <ImgTile src={images[0]} alt={`${altName} 1`} onClick={() => onOpenPopup(0)} className="absolute inset-0 w-full h-full" radius={radius} />
          )}
        </div>
        {/* Right: 2 stacked */}
        <div className="w-1/3 flex flex-col gap-1">
          {[0, 1].map(i => {
            const imgIndex = firstSlotIsVideo ? i : i + 1
            const img = images[imgIndex]
            if (!img) return <div key={i} className="flex-1 bg-gray-100 rounded-md" />
            const isLast = i === 1
            return (
              <div key={i} className={`flex-1 relative ${ratio}`} style={{ borderRadius: radius, overflow: 'hidden' }}>
                <ImgTile
                  src={img}
                  alt={`${altName} ${imgIndex + 1}`}
                  onClick={() => onOpenPopup(imgIndex)}
                  className="absolute inset-0 w-full h-full"
                  radius={radius}
                  overlay={isLast ? <CountOverlay count={images.length} countStyle={config.countStyle} dictionary={dictionary} /> : null}
                />
              </div>
            )
          })}
        </div>
        {/* Buttons */}
        <div className="absolute z-10 bottom-2 right-2">
          <MediaButtons config={config} onPhotos={() => onOpenPopup(0)} onVideo={onOpenVideo} videoUrl={videoUrl} vrTour={vrTour} blueprints={blueprints} dictionary={dictionary} />
        </div>
      </div>
      {/* Mobile */}
      <MobileSwiper images={images} altName={altName} onOpen={onOpenPopup} promotionTitle={promotionTitle} />
    </>
  )
}

// ─── Shared: mobile swiper ─────────────────────────────────────────────────────
function MobileSwiper({ images, altName, onOpen, promotionTitle }) {
  return (
    <div className="block sm:hidden">
      <Swiper loop={true} slidesPerView={1} modules={[Pagination]}>
        {images.map((img, i) => (
          <SwiperSlide key={i} onClick={() => onOpen(i)}>
            <div className="relative w-full aspect-[3/2] overflow-hidden">
              {i === 0 && promotionTitle && (
                <div className="absolute left-2 top-2 bg-third text-white px-2 py-1 rounded-md z-10 font-semibold text-sm">{promotionTitle}</div>
              )}
              <Image
                src={img}
                alt={`${altName} ${i + 1}`}
                fill
                className="object-cover"
                priority={i === 0}
                placeholder="blur"
                blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAAAyCAYAAACqNX6+AAAAXUlEQVR42u3RMQEAAAgDoJnc6FrB0wMqUMl0eKOECEGIEIQIQYgQhAgRIgQhQhAiBCFCECIEIQgRghAhCBGCECEIQYgQhAhBiBCECEEIQoQgRAhChCBECEIQIoSrBfgKSs//8LAxAAAAAElFTkSuQmCC"
              />
              <div className="absolute z-10 bottom-2 right-2 flex items-center bg-black/50 rounded-md text-white px-2 py-1 text-sm">
                <span>{i + 1}/{images.length}</span>
                <IoMdPhotos className="ml-1" />
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}

// ─── Main component ────────────────────────────────────────────────────────────
const PropertyGalleryBlock = ({
  property,
  config,        // gallery config from website.elements.gallery
  dictionary,
  promotionTitle,
}) => {
  const [activeSlide,      setActiveSlide]      = useState(0)
  const [isPopupOpen,      setPopupOpen]         = useState(false)
  const [isVideoPopupOpen, setVideoPopupOpen]    = useState(false)

  const images    = property.gallery ?? property.images ?? []
  const videoUrl  = property.videoUrl ?? null
  const vrTour    = property.vrTour   ?? null
  const blueprints = property.blueprints ?? []
  const altName   = property.name ?? ''

  const galleryConfig = config ?? {
    style:         'single',
    ratio:         '4/3',
    popupStyle:    'carousel',
    showVideo:     false,
    showBlueprint: false,
    showVR:        false,
  }

  const openPopup  = (index) => { setActiveSlide(index); setPopupOpen(true);  setVideoPopupOpen(false) }
  const openVideo  = ()      => { setVideoPopupOpen(true); setPopupOpen(false) }
  const closePopup = ()      => setPopupOpen(false)
  const closeVideo = ()      => setVideoPopupOpen(false)

  if (!images.length && !videoUrl) return (
    <div className={`relative w-full ${ratioClass(galleryConfig.ratio)} rounded-md overflow-hidden bg-gray-100`}>
      <Image src="/images/imageplaceholder.jpg" alt="No image" fill className="object-cover" />
    </div>
  )

  const radius = galleryConfig.imageRadius ?? '0.5rem'

  const sharedProps = {
    config:         galleryConfig,
    radius,
    images,
    videoUrl,
    vrTour,
    blueprints,
    promotionTitle,
    altName,
    onOpenPopup:    openPopup,
    onOpenVideo:    openVideo,
    dictionary,
  }

  const style = galleryConfig.style ?? 'single'

  return (
    <div className="relative w-full">
      {style === 'single'                           && <StyleSingle  {...sharedProps} />}
      {(style === 'multi_1_4' || style === 'multi_1_4_nov') && <StyleMulti4  {...sharedProps} />}
      {(style === 'multi_1_2' || style === 'multi_1_2_nov') && <StyleMulti2  {...sharedProps} />}

      <Gallery
        isOpen={isPopupOpen}
        onClose={closePopup}
        gallery={images}
        videoUrl={videoUrl}
        initialSlide={activeSlide}
        altName={altName}
        popupStyle={galleryConfig.popupStyle}
        imageRadius={galleryConfig.imageRadius ?? '0.5rem'}
      />
      <VideoGallery
        isVideoOpen={isVideoPopupOpen}
        onVideoClose={closeVideo}
        videoUrl={videoUrl}
      />
    </div>
  )
}

export default PropertyGalleryBlock